import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { CatalogEntityPage, CatalogIndexPage, catalogPlugin } from '@backstage/plugin-catalog';
import { CatalogImportPage, catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SearchPage } from '@backstage/plugin-search';
import { DefaultTechDocsHome, TechDocsIndexPage, techdocsPlugin, TechDocsReaderPage } from '@backstage/plugin-techdocs';
import { ExpandableNavigation, ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { Navigate, Route } from 'react-router-dom';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { Root } from './components/Root';

import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { AlertDisplay, OAuthRequestDialog, SignInPage } from '@backstage/core-components';
import { configApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { UnifiedThemeProvider } from '@backstage/theme';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';
import './styles.css';
import { wsaDarkTheme, wsaLightTheme } from './themes';

import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { AiGatewayKeysPage } from '@internal/backstage-plugin-ai-gateway-keys';
import { CcoeCloudOnboardingPage } from '@internal/backstage-plugin-ccoe-cloud-onboarding';
import { DeployNextAppPage } from '@internal/backstage-plugin-deploy-next-app';
import { DpeSubscriptionVendingPage } from '@internal/backstage-plugin-dpe-subscription-vending';
import { SystemOwnerDetailsCard } from '@internal/backstage-plugin-system-owner-details-card';
import { PlausibleProvider } from './PlausibleProvider';
import { RepositoryNamePreviewExtension } from './scaffolder/RepositoryNamePreview';
import { templateOwnerGroups } from './scaffolder/templateOwnerGroups';

const app = createApp({
  apis,
  themes: [
    {
      id: 'wsa-light',
      title: 'WSA Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => <UnifiedThemeProvider theme={wsaLightTheme}>{children}</UnifiedThemeProvider>,
    },
    {
      id: 'wsa-dark',
      title: 'WSA Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => <UnifiedThemeProvider theme={wsaDarkTheme}>{children}</UnifiedThemeProvider>,
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: (props) => {
      const configApi = useApi(configApiRef);
      if (configApi.getString('auth.environment') === 'development') {
        return (
          <SignInPage
            {...props}
            auto
            providers={[
              'guest',
              {
                id: 'azure-auth-provider',
                title: 'WSA SSO',
                message: 'Sign in with Microsoft',
                apiRef: microsoftAuthApiRef,
              },
            ]}
          />
        );
      }
      return (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'azure-auth-provider',
            title: 'WSA SSO',
            message: 'Sign in with Microsoft',
            apiRef: microsoftAuthApiRef,
          }}
        />
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome />
    </Route>
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        {/* ReportIssue add-on works only with GitHub or GitLab */}
        <ReportIssue />
        <ExpandableNavigation />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage groups={templateOwnerGroups} />}>
      <ScaffolderFieldExtensions>
        <RepositoryNamePreviewExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/deploy-next-app" element={<DeployNextAppPage />} />
    <Route path="/ccoe-cloud-onboarding" element={<CcoeCloudOnboardingPage />} />
    <Route path="/system-owner-details-card" element={<SystemOwnerDetailsCard />} />
    <Route path="/dpe-subscription-vending" element={<DpeSubscriptionVendingPage />} />
    <Route path="/ai-gateway-keys" element={<AiGatewayKeysPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay transientTimeoutMs={2000} />
    <OAuthRequestDialog />
    <AppRouter>
      <PlausibleProvider />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
